import { createElement, useEffect, useState, type ComponentType, type ReactNode } from 'react';
import { AsyncState } from './components/AsyncState';

/** Cache code only. Session props and record reads remain owned by each mounted workspace. */
export function deferWorkspace<Props extends object>(
  load: () => Promise<{ default: ComponentType<Props> }>,
  options?: {
    loadingMessage?: string;
    errorMessage?: string;
    offerReload?: boolean;
    loadingContent?: ReactNode;
    errorContent?: (retry: () => void) => ReactNode;
  },
) {
  let resolved: ComponentType<Props> | undefined;
  let pending: Promise<ComponentType<Props>> | undefined;
  function loadCode() {
    if (resolved) return Promise.resolve(resolved);
    if (pending) return pending;
    // A stalled module request must not leave the workspace spinning indefinitely.
    // Retry is explicit and loads code only; it never replays a record operation.
    let deadline: ReturnType<typeof setTimeout>;
    const timeout = new Promise<never>((_resolve, reject) => {
      deadline = setTimeout(() => reject(new Error('Workspace download timed out')), 30_000);
    });
    pending = Promise.race([Promise.resolve().then(load), timeout])
      .then(
        (module) => {
          resolved = module.default;
          pending = undefined;
          return resolved;
        },
        (error) => {
          pending = undefined;
          throw error;
        },
      )
      .finally(() => clearTimeout(deadline));
    return pending;
  }
  function DeferredWorkspace(props: Props) {
    const [attempt, setAttempt] = useState(0);
    const [state, setState] = useState<{ Component?: ComponentType<Props>; failed?: boolean }>(
      () => (resolved ? { Component: resolved } : {}),
    );
    useEffect(() => {
      let active = true;
      void loadCode().then(
        (Component) => {
          if (active) setState({ Component });
        },
        () => {
          if (active) setState({ failed: true });
        },
      );
      return () => {
        active = false;
      };
    }, [attempt]);
    if (state.Component) return createElement(state.Component, props);
    if (state.failed && options?.errorContent)
      return options.errorContent(() => {
        setState({});
        setAttempt((value) => value + 1);
      });
    if (state.failed)
      return createElement(
        'section',
        { className: 'content-section', 'aria-label': 'Workspace download recovery' },
        createElement(AsyncState, {
          state: 'error',
          message:
            options?.errorMessage ??
            'This workspace could not be downloaded. Check your connection and try again. No save was submitted by this retry.',
          onRetry: () => {
            setState({});
            setAttempt((value) => value + 1);
          },
        }),
        options?.offerReload === false
          ? null
          : createElement(
              'p',
              null,
              'If a newer release is available, reload the app. Unsaved entries will be lost; check any recent save before submitting it again.',
            ),
        options?.offerReload === false
          ? null
          : createElement(
              'button',
              {
                type: 'button',
                className: 'button button--quiet',
                onClick: () => window.location.reload(),
              },
              'Reload app',
            ),
      );
    if (options?.loadingContent) return options.loadingContent;
    return createElement(
      'section',
      { className: 'content-section' },
      createElement(AsyncState, {
        state: 'loading',
        message: options?.loadingMessage ?? 'Downloading workspace…',
      }),
    );
  }
  // Intent prefetch downloads code only. It neither mounts a workspace nor reads
  // records, and shares the same bounded promise as a subsequent navigation.
  return Object.assign(DeferredWorkspace, {
    preload: async () => {
      try {
        await loadCode();
      } catch {
        // Navigation retains its visible recovery path; speculative failures
        // must not create an unhandled rejection or expose a download URL.
      }
    },
  });
}
